import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { config } from '../Constants';
import Seo from '../components/Seo';

const API = `${config.url}/api/v1/email`;

/**
 * Where "Unsubscribe from product updates" in an email lands (#365). The token in the link is the
 * only credential - nobody has to sign in to stop an email. Opening the page changes nothing:
 * mail scanners follow links, so it takes a press. Restock alerts are untouched either way.
 */
const UnsubscribePage = () => {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const [state, setState] = useState({ status: 'loading', maskedEmail: '', optedOut: false });
    const [busy, setBusy] = useState(false);

    const call = async (path, method) => {
        const response = await fetch(`${API}/${path}/${encodeURIComponent(token)}`, { method });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || !body.data) {
            throw new Error(body.message || 'This link is not valid any more');
        }
        return body.data;
    };

    useEffect(() => {
        let cancelled = false;
        if (!token) {
            setState({ status: 'invalid', maskedEmail: '', optedOut: false });
            return undefined;
        }
        call('preferences', 'GET')
            .then((data) => { if (!cancelled) { setState({ status: 'ready', ...data }); } })
            .catch(() => { if (!cancelled) { setState({ status: 'invalid', maskedEmail: '', optedOut: false }); } });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const change = async (path) => {
        setBusy(true);
        try {
            const data = await call(path, 'POST');
            setState({ status: 'ready', ...data, justChanged: true });
        } catch (error) {
            setState({ status: 'invalid', maskedEmail: '', optedOut: false });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 560, margin: '0 auto', padding: '48px 24px' }}>
            <Seo title="Email preferences | StockMaid" description="Manage StockMaid product update emails." canonicalPath="/unsubscribe" robots="noindex,nofollow" />
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                {state.status === 'loading' && <CircularProgress size={28} />}

                {state.status === 'invalid' && (
                    <>
                        <Typography variant="h5" gutterBottom>This link isn't valid any more</Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Use the unsubscribe link from your most recent StockMaid email, or contact us and we'll take care of it.
                        </Typography>
                        <Button variant="outlined" component="a" href="mailto:support@stockmaid.com">Contact us</Button>
                    </>
                )}

                {state.status === 'ready' && !state.optedOut && (
                    <>
                        <Typography variant="h5" gutterBottom>
                            {state.justChanged ? "You're subscribed again" : 'Unsubscribe from product updates?'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                            {state.maskedEmail}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Product updates are the occasional email about what's new on StockMaid.
                            Your restock alerts are separate and won't change.
                        </Typography>
                        <Button variant="contained" onClick={() => change('unsubscribe')} disabled={busy}>
                            Unsubscribe
                        </Button>
                    </>
                )}

                {state.status === 'ready' && state.optedOut && (
                    <>
                        <Typography variant="h5" gutterBottom>You're unsubscribed</Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                            {state.maskedEmail} won't get product updates from StockMaid.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            You'll still get the restock alerts you asked for.
                        </Typography>
                        <Button variant="text" onClick={() => change('resubscribe')} disabled={busy}>
                            That was a mistake - subscribe me again
                        </Button>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default UnsubscribePage;
